// Where the emailed link lands. The token in the query string is the whole
// credential - the visitor arrives with no session behind it - so the screen
// asks for nothing else, and setting the password signs them in.

import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { setPortalPassword } from "../../api/clientPortal";
import { MIN_PASSWORD_LENGTH } from "../../api/auth";
import { PortalAuthBrand } from "../../components/portal/PortalAuthBrand";

export default function PortalSetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== repeat) {
      setError("The two passwords do not match.");
      return;
    }

    setSubmitting(true);
    setError(null);
    const result = await setPortalPassword(token, password);
    if (result.ok) {
      navigate("/portal", { replace: true });
      return;
    }
    setSubmitting(false);
    setError(result.error);
  }

  if (!token) {
    return (
      <main className="portal-auth">
        <div className="portal-auth-card">
          <div className="portal-auth-head">
            <PortalAuthBrand />
            <h1>Choose your password</h1>
            <p>
              This link is missing the part that identifies you. Open it again from our email, or
              call our office and we will send a new one.
            </p>
          </div>
          <div className="portal-auth-alts">
            <Link className="portal-link-btn" to="/portal/login">
              Back to sign in
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="portal-auth">
      <form className="portal-auth-card" onSubmit={onSubmit}>
        <div className="portal-auth-head">
          <PortalAuthBrand />
          <h1>Choose your password</h1>
          <p>This is the password you will use from now on to sign in and follow your case.</p>
        </div>

        {error && (
          <div className="portal-auth-error" role="alert">
            {error}
          </div>
        )}

        <label className="portal-field">
          <span>New password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-label="New password"
            autoComplete="new-password"
            required
          />
          <small>At least {MIN_PASSWORD_LENGTH} characters.</small>
        </label>

        <label className="portal-field">
          <span>Repeat password</span>
          <input
            type="password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>

        <button type="submit" className="portal-auth-submit" disabled={submitting}>
          {submitting ? "Saving..." : "Save and sign in"}
        </button>

        {/* A spent link and a browser that refuses to keep the session both
            end here, and both are recoverable from the sign-in page. */}
        <div className="portal-auth-alts">
          <Link className="portal-link-btn" to="/portal/login">
            Back to sign in
          </Link>
        </div>

        <p className="portal-auth-foot">
          The link in your email works once and expires after a day. If it has expired, ask for a
          new one from the sign-in page.
        </p>
      </form>
    </main>
  );
}
